import { Vec3, type ViewerAnnotation } from '@mindcloudxai_3d/core';
import type { Hotspot, TaskData } from '@app-types/scene';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function parseJsonMaybe(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

function toVec3(value: unknown): Vec3 | null {
  const parsed = parseJsonMaybe(value);
  if (Array.isArray(parsed)) {
    const [x, y, z] = parsed.map(finiteNumber);
    if (x == null || y == null || z == null) return null;
    return new Vec3(x, y, z);
  }

  const record = asRecord(parsed);
  if (!record) return null;
  const x = finiteNumber(record.x);
  const y = finiteNumber(record.y);
  const z = finiteNumber(record.z);
  if (x === null || y === null || z === null) return null;
  return new Vec3(x, y, z);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function normalizeHotspot(value: unknown, index: number): Hotspot | null {
  const record = asRecord(parseJsonMaybe(value));
  if (!record) return null;

  const position = toVec3(record.position ?? record.cameraPosition ?? record.camera);
  if (!position) return null;

  const lookAt = toVec3(record.lookAt ?? record.target ?? record.anchor) ?? undefined;
  const title = stringValue(record.title ?? record.name) ?? `Hotspot ${index + 1}`;

  return {
    id: stringValue(record.id) ?? `hotspot-${index + 1}`,
    position,
    lookAt,
    title,
    description: stringValue(record.description ?? record.desc),
    imageUrl: stringValue(record.imageUrl ?? record.image ?? record.cover),
  };
}

function normalizeHotspotList(value: unknown): Hotspot[] {
  const parsed = parseJsonMaybe(value);
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((item, index) => {
    const hotspot = normalizeHotspot(item, index);
    return hotspot ? [hotspot] : [];
  });
}

export function shouldUseDevMockHotspots(): boolean {
  if (!import.meta.env.DEV && import.meta.env.VITE_APP_ENV !== 'dev') return false;
  if (typeof window === 'undefined') return false;
  const value = new URLSearchParams(window.location.search).get('mockHotspots');
  return value === '1' || value === 'true' || value === 'yes';
}

export function createDevMockHotspots(sceneKey: string): Hotspot[] {
  return [
    {
      id: `${sceneKey}-overview`,
      position: new Vec3(0, 1.8, 4.2),
      lookAt: new Vec3(0, 0.8, 0),
      title: 'Overview',
      description: 'A wide framing point for quickly returning to the complete scene composition.',
      imageUrl:
        'https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=640&q=80',
    },
    {
      id: `${sceneKey}-left-detail`,
      position: new Vec3(-2.6, 1.4, 2.2),
      lookAt: new Vec3(-0.8, 0.6, 0),
      title: 'Left detail',
      description: 'A closer inspection angle for validating nearby geometry and splat density.',
      imageUrl:
        'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=640&q=80',
    },
    {
      id: `${sceneKey}-right-detail`,
      position: new Vec3(2.4, 1.5, 2.1),
      lookAt: new Vec3(0.8, 0.6, 0),
      title: 'Right detail',
      description: 'A secondary point of interest used by the hotspot navigation mock flow.',
      imageUrl:
        'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=640&q=80',
    },
  ];
}

export function resolveSceneHotspots(task: TaskData): Hotspot[] {
  const candidates = [
    task.metadata?.hotspots,
    task.extra_fields?.hotspots,
    task.model_config?.hotspots,
    task.extra_fields_str,
  ];

  for (const candidate of candidates) {
    const parsed = parseJsonMaybe(candidate);
    const fromList = normalizeHotspotList(parsed);
    if (fromList.length > 0) return fromList;

    const record = asRecord(parsed);
    const nested = normalizeHotspotList(record?.hotspots);
    if (nested.length > 0) return nested;
  }

  return shouldUseDevMockHotspots() ? createDevMockHotspots(task.task_code) : [];
}

export function resolveDirectUrlHotspots(sceneKey: string): Hotspot[] {
  return shouldUseDevMockHotspots() ? createDevMockHotspots(sceneKey) : [];
}

/** 热点在场景中的锚点：有 lookAt 时 position 是机位，lookAt 才是被观察物。 */
export function getHotspotAnchor(hotspot: Hotspot): Vec3 {
  return hotspot.lookAt ?? hotspot.position;
}

/**
 * Hotspot → Viewer 标注输入。语义映射：有 lookAt 时 hotspot.position 是相机机位，
 * lookAt 是锚点；无 lookAt 时 position 即锚点，机位由 viewer 运行时推导。
 * 顺序与 SceneConfig.hotspots 严格对齐，focusAnnotation(index) 依赖此索引。
 */
export function toViewerAnnotations(hotspots: Hotspot[]): ViewerAnnotation[] {
  return hotspots.map((hotspot) => ({
    position: getHotspotAnchor(hotspot),
    title: hotspot.title,
    text: hotspot.description,
    camera: hotspot.lookAt ? { position: hotspot.position, target: hotspot.lookAt } : undefined,
  }));
}
